import React, { useState } from "react";
import {
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { ApplyForProjectPayload } from "../../lib/api/projects";

interface ProjectApplicationModalProps {
  projectTitle: string;
  verificationLink: string;
  onClose: () => void;
  onSubmit: (
    payload: ApplyForProjectPayload
  ) => Promise<{ verificationLink: string }>;
  isPending: boolean;
}

const AVAILABILITY_OPTIONS = [
  { value: "5-10", label: "5-10 hours per week" },
  { value: "10-20", label: "10-20 hours per week" },
  { value: "20-30", label: "20-30 hours per week" },
  { value: "30+", label: "30+ hours per week" },
];

const ProjectApplicationModal: React.FC<ProjectApplicationModalProps> = ({
  projectTitle,
  verificationLink,
  onClose,
  onSubmit,
  isPending,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    availability: "",
    acceptedTerms: false,
    acceptedGuidelines: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [returnedVerificationLink, setReturnedVerificationLink] = useState("");

  const validateStep = (currentStep: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (currentStep === 1) {
      if (!formData.availability) {
        newErrors.availability = "Please select your availability";
      } else if (
        !["5-10", "10-20", "20-30", "30+"].includes(formData.availability)
      ) {
        newErrors.availability = "Please select a valid availability option";
      }

      if (!formData.acceptedTerms) {
        newErrors.acceptedTerms = "You must accept the terms and conditions";
      }
      if (!formData.acceptedGuidelines) {
        newErrors.acceptedGuidelines = "You must accept the quality guidelines";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;

    try {
      const result = await onSubmit({
        availability: formData.availability,
        acceptedTerms: formData.acceptedTerms,
        acceptedGuidelines: formData.acceptedGuidelines,
      });
      setReturnedVerificationLink(result.verificationLink);
      setStep(2);
    } catch {
      // Error handled by parent
    }
  };

  const handleRedirectToVerification = () => {
    window.open(returnedVerificationLink || verificationLink, "_blank");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {step === 2 ? "Application Submitted!" : "Apply for Project"}
              </h2>
              {step !== 2 && (
                <p className="text-sm text-gray-500 mt-1">{projectTitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Availability & Terms */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Warning Banner */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">Important</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    After submitting, you will be redirected to complete
                    document verification. Please ensure you're ready to
                    complete this step before proceeding.
                  </p>
                </div>
              </div>

              {/* Availability */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekly availability <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.availability}
                  onChange={(e) =>
                    setFormData({ ...formData, availability: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white ${
                    errors.availability ? "border-red-300" : "border-gray-200"
                  }`}
                >
                  <option value="">Select your availability...</option>
                  {AVAILABILITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.availability && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.availability}
                  </p>
                )}
              </div>

              {/* Terms & Guidelines */}
              <div className="space-y-3">
                <label
                  className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition-colors ${
                    formData.acceptedTerms
                      ? "border-[#8B52FF] bg-[#8B52FF]/5"
                      : errors.acceptedTerms
                        ? "border-red-300"
                        : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.acceptedTerms}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        acceptedTerms: e.target.checked,
                      })
                    }
                    className="mt-0.5 w-4 h-4 text-[#8B52FF] rounded focus:ring-[#8B52FF]"
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      Terms and Conditions{" "}
                      <span className="text-red-500">*</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      I agree to the platform's terms of service and understand
                      my responsibilities as a contributor.
                    </p>
                  </div>
                </label>
                {errors.acceptedTerms && (
                  <p className="text-sm text-red-500 ml-1">
                    {errors.acceptedTerms}
                  </p>
                )}

                <label
                  className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition-colors ${
                    formData.acceptedGuidelines
                      ? "border-[#8B52FF] bg-[#8B52FF]/5"
                      : errors.acceptedGuidelines
                        ? "border-red-300"
                        : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.acceptedGuidelines}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        acceptedGuidelines: e.target.checked,
                      })
                    }
                    className="mt-0.5 w-4 h-4 text-[#8B52FF] rounded focus:ring-[#8B52FF]"
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      Quality Guidelines <span className="text-red-500">*</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      I agree to follow the project guidelines and quality
                      standards.
                    </p>
                  </div>
                </label>
                {errors.acceptedGuidelines && (
                  <p className="text-sm text-red-500 ml-1">
                    {errors.acceptedGuidelines}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Success */}
          {step === 2 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Application Submitted Successfully!
              </h3>
              <p className="text-gray-500 mb-6">
                Your application is now pending review. Please complete the
                document verification to proceed.
              </p>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
                <p className="text-sm text-blue-700">
                  Click the button below to complete your document verification.
                  Prepare either your passport or driver's license then proceed
                </p>
              </div>

              <button
                onClick={handleRedirectToVerification}
                className="w-full py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Complete Verification
              </button>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        {step === 1 && (
          <div className="flex-shrink-0 p-6 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 border border-gray-200 rounded-xl"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectApplicationModal;
